import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetNotification } from '../../../context/Notification';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { CreateLeadNotePayload, LeadDetailsResponse, LeadNotePayload } from '../types';
import { LEAD_DETAILS } from './constants';

// HWM-22633 round 4: server-confirmed cache patch. On success the response
// IS the new `latest_note`, so the lead-detail cache (and any consumer
// holding the row via the caller-supplied `onSuccess`) is patched directly
// — no `invalidateQueries`, no refetch.
const useCreateLeadNote = (onSuccess?: (data: LeadNotePayload) => void) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();

    const request = ({ payload }: { payload: CreateLeadNotePayload }) => {
        const { lead_id, body } = payload;
        return req({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/leads/${lead_id}/notes/`,
            method: 'POST',
            body: JSON.stringify({ body })
        }) as Promise<LeadNotePayload>;
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (data, vars) => {
            queryClient.setQueryData<LeadDetailsResponse | undefined>(
                [LEAD_DETAILS, vars.payload.lead_id],
                (old) => (old ? { ...old, latest_note: data } : old)
            );
            setNotification({ message: 'Note added' });
            onSuccess && onSuccess(data);
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default useCreateLeadNote;
